import { useQueryClient } from '@tanstack/react-query';
import { memo, type ReactElement, useMemo, useState } from 'react';
import { useAuth } from '@/auth/useAuth';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ExecutionOutputTableCell } from '@/features/executions/components/ExecutionOutputTableCell';
import { buildExecutionOutputValues } from '@/features/executions/components/executionOutputTableCellUtils';
import { SuiteRunEditDialog } from '@/features/executions/components/SuiteRunEditDialog';
import { useSuiteRunsTableRowActions } from '@/features/executions/components/useSuiteRunsTableRowActions';
import { updateSuiteRun } from '@/features/executions/data/suiteRuns';
import { buildSuiteRunParentInvalidations } from '@/features/executions/queries/executionInvalidationFilters';
import { suiteRunsGridConfig } from '@/features/executions/queries/executionsConfig';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects/hooks/projectHooks';
import { TableBreakdownLine } from '@/features/shared/components/tableSystem/TableBreakdownLine';
import {
  TableActionsCell,
  TableCellContainer,
} from '@/features/shared/components/tableSystem/TableCells';
import { tableColumnWidths } from '@/features/shared/components/tableSystem/tableColumnWidths';
import {
  TABLE_ROW_HEIGHT,
  TABLE_ROW_MEASURE_HEIGHT,
} from '@/features/shared/components/tableSystem/tableConstants';
import {
  resolveLatestChipBackground,
  resolveLatestChipForeground,
} from '@/features/shared/components/tableSystem/TableLatestCellStyles';
import {
  CLONE_META_DETAIL_GLYPH_SX,
  CLONE_META_DETAILS_LINK_SX as EXECUTION_DETAILS_LINK_SX,
  STATUS_META_TIMESTAMP_CONTENT_SX,
  STATUS_META_TIMESTAMP_VALUE_WRAPPER_SX,
  TableBreakdownStack,
  TableStatusMetaContent,
} from '@/features/shared/components/tableSystem/TableMetaCells';
import {
  buildExecutionRunDetailPath,
  buildResultsFileDetailPath,
  buildSuiteDetailPath,
  buildSuiteRunPath,
} from '@/features/shared/components/tableSystem/tableRoutes';
import { TableStatusBreakdownCell } from '@/features/shared/components/tableSystem/TableStatusBreakdownCell';
import { tableSx } from '@/features/shared/components/tableSystem/tableSx';
import { useDetailEditEditor } from '@/features/shared/hooks/useDetailEditEditor';
import { useProjectStatusPresentation } from '@/features/shared/hooks/useProjectStatusPresentation';
import {
  buildTagColorByName,
  mergeTagColorByName,
  resolveEntityTags,
  resolveTagColor,
} from '@/features/shared/tags';
import {
  resolveProjectDisplayStatus,
  resolveProjectDisplayStatusLabel,
} from '@/features/shared/utils/projectStatusPresentation';
import {
  resolveSuiteEntityDisplayName,
  resolveSuiteEntitySecondaryLabel,
} from '@/features/suites/utils/suiteDisplayName';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { getDetailLabel } from '@/shared/copy/details';
import { getDialogMessage } from '@/shared/copy/dialogs';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { patchQueryResultCaches } from '@/shared/data/queryResultCache';
import {
  gridQueryKey,
  gridPaths,
  invalidateGridQuery,
  invalidateSuiteRunDetail,
  invalidateSuiteRuns,
  invalidateTestRuns,
  queryKeys,
  useInvalidateQueries,
  useTagOptions,
} from '@/shared/hooks';
import { r } from '@/theme/tokens';
import { formatDuration } from '@/ui';
import { buildTableGridProps } from '@/ui/composites/grid/tableGridProps';
import { ServerDataTable } from '@/ui/composites/ServerDataTable';
import { getStatusIconComponent, RunLinkCell, TableNameCell } from '@/ui/composites/table';
import { mapTestRunSummariesWithRunNumbers } from '@/ui/composites/table/tableUtils';
import { formatDate } from '@/ui/formatters/formatDate';
import { formatRelativeTime } from '@/ui/formatters/formatRelativeTime';
import {
  Box,
  type DataGridProps,
  type GridColDef,
  type GridColumnVisibilityModel,
  type GridFilterModel,
  type GridSortModel,
  resolveStatusChipTone,
} from '@/ui/primitives';
import {
  DescriptionOutlinedIcon,
  EventOutlinedIcon,
  MoreVertIcon,
  PlayCircleFilledWhiteOutlinedIcon,
  TimerOutlinedIcon,
} from '@/ui/primitives/icons';
import { type QueryResult, type SuiteRunDto } from '@flowqa/shared';
import { resolveSuiteRunTestCounts } from './suiteRuns.testCounts';
import type { TagColor, TagOption } from '@/shared/data/tags';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { QueryKey } from '@tanstack/react-query';
type SuiteRunsTableProps = {
  persistKey?: string | null;
  syncStateToUrl?: boolean;
  pageSizeOptions?: number[];
  initialPageSize?: number;
  disablePageSizeSelect?: boolean;
  hideFooter?: boolean;
  disableColumnSorting?: boolean;
  disableColumnMenu?: boolean;
  showToolbar?: boolean;
  noRowsText?: string;
  fixedFilters?: GridQueryInput['filters'];
  initialSortModel?: GridSortModel;
  initialFilterModel?: GridFilterModel;
  queryFn?: (input: GridQueryInput) => Promise<QueryResult<SuiteRunDto>>;
  queryKey?: QueryKey;
  initialData?: QueryResult<SuiteRunDto>;
  onRowsChange?: (rows: SuiteRunDto[]) => void;
  onQueryMeta?: (meta: { total: number; items: SuiteRunDto[] } | null) => void;
  onQueryInput?: (input: GridQueryInput) => void;
  onDataUpdatedAt?: (timestamp: number | null) => void;
  showLastUpdated?: boolean;
  disableLinks?: boolean;
  disableSummaryLinks?: boolean;
  isAuthenticatedOverride?: boolean;
  includeSummary?: boolean;
  search?: string;
  columnVisibilityModel?: GridColumnVisibilityModel;
  onColumnVisibilityModelChange?: DataGridProps['onColumnVisibilityModelChange'];
  tagColorByName?: Partial<Record<string, TagColor>>;
  tagOptionsOverride?: TagOption[];
  hideNameColumn?: boolean;
  hideDetailsColumn?: boolean;
  runColumnFirst?: boolean;
  disableActionsPopover?: boolean;
  disableOutputTooltip?: boolean;
  hideActionsColumn?: boolean;
  showPaginationSkeletonWhenRowsVisible?: boolean;
  preservePreviousData?: boolean;
  dynamicRowHeight?: boolean;
  rowHeight?: number;
  active?: boolean;
};
const SuiteRunsTableComponent = ({
  persistKey,
  syncStateToUrl = false,
  pageSizeOptions = [5, 10, 25, 50],
  initialPageSize,
  disablePageSizeSelect,
  hideFooter,
  disableColumnSorting = true,
  disableColumnMenu = true,
  showToolbar = true,
  noRowsText = getEmptyStateMessage('entity.noneFound', { entity: 'suiteRun' }),
  fixedFilters,
  initialSortModel,
  initialFilterModel,
  queryFn,
  queryKey,
  initialData,
  onRowsChange,
  onQueryMeta,
  onQueryInput,
  onDataUpdatedAt,
  showLastUpdated,
  disableLinks = false,
  disableSummaryLinks = false,
  isAuthenticatedOverride,
  includeSummary = false,
  search,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  tagColorByName = {},
  tagOptionsOverride,
  hideNameColumn = false,
  hideDetailsColumn = false,
  runColumnFirst = false,
  disableActionsPopover = false,
  disableOutputTooltip = false,
  hideActionsColumn = false,
  showPaginationSkeletonWhenRowsVisible = false,
  preservePreviousData = false,
  dynamicRowHeight = true,
  rowHeight,
  active = true,
}: SuiteRunsTableProps): ReactElement => {
  const resolvedRowHeight = rowHeight ?? TABLE_ROW_HEIGHT;
  const resolvedEstimatedRowHeight = rowHeight ?? TABLE_ROW_MEASURE_HEIGHT;
  const columnWidths = tableColumnWidths.suiteRuns;
  const { session } = useAuth();
  const isAuthenticated = isAuthenticatedOverride ?? Boolean(session?.userId);
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId, setSelectedProjectId } = useProjectFilter();
  const { groupErroredWithFailed, preferFileNameOverDisplayName } = useProjectStatusPresentation();
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  const [editTarget, setEditTarget] = useState<SuiteRunDto | null>(null);
  const { tagOptions: fetchedTagOptions } = useTagOptions({
    projectId: selectedProjectId,
    enabled: tagOptionsOverride === undefined,
  });
  const {
    deleteLoading,
    deleteRowPendingId,
    deleteTarget,
    onDeleteClose,
    onDeleteConfirm,
    onDeleteRow,
    updateRowTagsColorPendingId,
  } = useSuiteRunsTableRowActions({ selectedProjectId });
  const editEditor = useDetailEditEditor<SuiteRunDto['status']>({
    projectId: editTarget?.projectId ?? null,
    successMessage: 'Suite run updated',
    saveEntity: async ({ tags, status, timestamp, durationMs }) => {
      if (!editTarget) return;
      const updated = await updateSuiteRun(
        { id: editTarget.id, tags, status, timestamp, durationMs },
        editTarget.organisationId ?? selectedOrganisationId ?? null,
      );
      patchQueryResultCaches(
        queryClient,
        [
          {
            queryKey: queryKeys.suiteRuns(
              selectedProjectId ?? null,
              editTarget.organisationId ?? null,
            ),
          },
          { queryKey: gridQueryKey(gridPaths.suiteRuns) },
        ],
        updated,
      );
      const invalidations = [
        ...buildSuiteRunParentInvalidations(editTarget),
        invalidateSuiteRuns(selectedProjectId ?? null, editTarget.organisationId ?? null),
        invalidateSuiteRunDetail(editTarget.id, editTarget.organisationId ?? null),
        invalidateTestRuns(selectedProjectId ?? null, editTarget.organisationId ?? null),
        invalidateGridQuery(gridPaths.suiteRuns),
        invalidateGridQuery(gridPaths.testRuns),
      ];
      await invalidateQueries(invalidations);
    },
  });
  const resolvedTagOptions = tagOptionsOverride ?? fetchedTagOptions;
  const resolvedTagColorByName = useMemo(
    () => buildTagColorByName(resolvedTagOptions),
    [resolvedTagOptions],
  );
  const mergedTagColorByName = useMemo(
    () => mergeTagColorByName(resolvedTagColorByName, tagColorByName),
    [resolvedTagColorByName, tagColorByName],
  );
  const columns = useMemo<Array<GridColDef<SuiteRunDto>>>(
    () => [
      {
        field: 'name',
        headerName: 'Suite',
        flex: columnWidths.name.flex,
        align: 'left',
        headerAlign: 'left',
        sortable: false,
        filterable: false,
        cellClassName: 'table-name-cell',
        valueGetter: (_value, row) =>
          resolveSuiteEntityDisplayName(
            {
              name: row.suiteName ?? row.name ?? row.suiteId,
              displayName: row.displayName,
            },
            { preferFileNameOverDisplayName },
          ),
        renderCell: (params) => {
          const primaryLabel = resolveSuiteEntityDisplayName(
            {
              name: params.row.suiteName ?? params.row.name ?? params.row.suiteId,
              displayName: params.row.displayName,
            },
            { preferFileNameOverDisplayName },
          );
          const secondaryLabel = resolveSuiteEntitySecondaryLabel(
            {
              name: params.row.suiteName ?? params.row.name ?? params.row.suiteId,
              displayName: params.row.displayName,
            },
            { preferFileNameOverDisplayName },
          );
          const tags = resolveEntityTags(params.row);
          const suiteHref = disableLinks
            ? null
            : buildSuiteDetailPath(params.row.suiteSlug ?? params.row.suiteId);
          const resolvedTags = tags.map((tag) => ({
            label: tag,
            tone: resolveTagColor(tag, mergedTagColorByName),
          }));
          return (
            <TableCellContainer gap="none">
              <TableNameCell
                primary={{
                  label: primaryLabel,
                  ...(suiteHref
                    ? {
                        href: suiteHref,
                        onClick: () => {
                          setSelectedProjectId(params.row.projectId);
                        },
                      }
                    : {}),
                }}
                secondary={
                  secondaryLabel
                    ? {
                        label: secondaryLabel,
                        ...(suiteHref
                          ? {
                              href: suiteHref,
                              onClick: () => {
                                setSelectedProjectId(params.row.projectId);
                              },
                            }
                          : {}),
                      }
                    : null
                }
                tags={resolvedTags}
              />
            </TableCellContainer>
          );
        },
      },
      {
        field: 'runNumber',
        headerName: getDetailLabel('run'),
        flex: columnWidths.run.flex,
        align: 'left',
        headerAlign: 'left',
        sortable: false,
        filterable: false,
        cellClassName: 'table-run-link-cell',
        renderCell: (params) => {
          const runLabel =
            typeof params.row.runNumber === 'number' ? `#${params.row.runNumber}` : 'View';
          const href =
            buildSuiteRunPath(params.row.suiteSlug ?? params.row.suiteId, params.row.runNumber) ??
            '/suites';
          return (
            <TableCellContainer gap="none">
              <RunLinkCell
                label={runLabel}
                href={disableLinks ? null : href}
                latest={params.row.isLatest === true}
                onClick={() => {
                  setSelectedProjectId(params.row.projectId);
                }}
              />
            </TableCellContainer>
          );
        },
      },
      {
        field: 'status',
        headerName: getDetailLabel('status'),
        flex: columnWidths.status.flex,
        align: 'left',
        headerAlign: 'left',
        sortable: false,
        filterable: false,
        cellClassName: 'table-status-meta-cell',
        renderCell: (params) => {
          const displayStatus = resolveProjectDisplayStatus(
            params.row.status,
            groupErroredWithFailed,
          );
          const statusTone = resolveStatusChipTone(displayStatus);
          const statusLabel =
            resolveProjectDisplayStatusLabel(params.row.status, groupErroredWithFailed) || '-';
          const suiteRunHref = disableLinks
            ? null
            : buildSuiteRunPath(params.row.suiteSlug ?? params.row.suiteId, params.row.runNumber);
          const durationLabel = formatDuration(params.row.durationMs ?? null);
          const timestamp = params.row.timestamp;
          const timestampLabel = formatRelativeTime(timestamp);
          const timestampTitle = formatDate(timestamp, { includeTime: true });
          const resolvedTimestampTitle = timestampTitle || timestamp.toISOString();
          return (
            <TableCellContainer gap="none">
              <TableStatusMetaContent
                statusLabel={statusLabel}
                statusHref={suiteRunHref}
                onStatusClick={(event) => {
                  event.stopPropagation();
                  setSelectedProjectId(params.row.projectId);
                }}
                chipTone={statusTone}
                chipIcon={<Box component={getStatusIconComponent(displayStatus)} />}
                chipBackgroundColor={(theme) => resolveLatestChipBackground(theme, statusTone)}
                chipForegroundColor={(theme) => resolveLatestChipForeground(theme, statusTone)}
                durationLabel={durationLabel}
                durationIcon={<TimerOutlinedIcon sx={CLONE_META_DETAIL_GLYPH_SX} />}
                timestampLabel={timestampLabel}
                timestampTitle={resolvedTimestampTitle}
                timestampIcon={<EventOutlinedIcon sx={CLONE_META_DETAIL_GLYPH_SX} />}
              />
            </TableCellContainer>
          );
        },
      },
      {
        field: 'tests',
        headerName: getDetailLabel('tests'),
        flex: columnWidths.tests.flex,
        align: 'left',
        headerAlign: 'left',
        sortable: false,
        filterable: false,
        cellClassName: 'table-breakdown-cell',
        renderCell: (params) => {
          const counts = resolveSuiteRunTestCounts(params.row);
          return (
            <TableCellContainer gap="none">
              <TableStatusBreakdownCell
                entity="test"
                counts={counts}
                summaryItems={mapTestRunSummariesWithRunNumbers(
                  params.row.testRunSummaries,
                  'display',
                )}
                disableSummaryLinks={disableSummaryLinks}
              />
            </TableCellContainer>
          );
        },
      },
      {
        field: 'output',
        headerName: getDetailLabel('output'),
        flex: columnWidths.output.flex,
        align: 'left',
        headerAlign: 'left',
        sortable: false,
        filterable: false,
        cellClassName: 'table-meta-details-cell',
        renderCell: (params) => (
          <ExecutionOutputTableCell
            output={buildExecutionOutputValues(params.row.systemOut, params.row.systemErr)}
            disableTooltip={disableOutputTooltip}
          />
        ),
      },
      {
        field: 'runId',
        headerName: getDetailLabel('details'),
        flex: columnWidths.details.flex,
        align: 'left',
        headerAlign: 'left',
        sortable: false,
        filterable: false,
        cellClassName: 'table-meta-details-cell',
        renderCell: (params) => {
          const uploadedLabel = formatRelativeTime(params.row.createdAt);
          const uploadedTitle = formatDate(params.row.createdAt, { includeTime: true });
          const resultsFileLabel =
            params.row.resultsFile?.fileName ?? params.row.resultsFileId ?? '-';
          const resultsFileHref = buildResultsFileDetailPath(params.row.resultsFileId);
          return (
            <TableCellContainer gap="none">
              <TableBreakdownStack>
                <TableBreakdownLine
                  rowTitle="Execution Run"
                  icon={
                    <PlayCircleFilledWhiteOutlinedIcon
                      sx={{ fontSize: (theme) => theme.app.iconSizes.sm }}
                    />
                  }
                  content={params.row.runId}
                  href={buildExecutionRunDetailPath(params.row.runId)}
                  disableLinks={disableLinks}
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                  linkSx={EXECUTION_DETAILS_LINK_SX}
                />
                <TableBreakdownLine
                  rowTitle="Results File"
                  icon={
                    <DescriptionOutlinedIcon sx={{ fontSize: (theme) => theme.app.iconSizes.sm }} />
                  }
                  content={resultsFileLabel}
                  href={resultsFileHref}
                  disableLinks={disableLinks}
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                  linkSx={EXECUTION_DETAILS_LINK_SX}
                />
                <TableBreakdownLine
                  rowTitle="Uploaded"
                  icon={<EventOutlinedIcon sx={{ fontSize: (theme) => theme.app.iconSizes.sm }} />}
                  content={uploadedLabel || '-'}
                  valueTitle={uploadedTitle || ''}
                  contentSx={STATUS_META_TIMESTAMP_CONTENT_SX}
                  valueWrapperSx={STATUS_META_TIMESTAMP_VALUE_WRAPPER_SX}
                />
              </TableBreakdownStack>
            </TableCellContainer>
          );
        },
      },
      {
        field: 'actions',
        headerName: '',
        flex: columnWidths.actions.flex,
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          if (disableActionsPopover) {
            return (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                  pt: '8px',
                }}
              >
                <Box
                  aria-hidden
                  sx={{
                    width: '28px',
                    height: '28px',
                    minWidth: '28px',
                    minHeight: '28px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRadius: r('pill'),
                    color: 'text.secondary',
                  }}
                >
                  <MoreVertIcon />
                </Box>
              </Box>
            );
          }

          return (
            <TableActionsCell
              rowName={params.row.suiteName ?? params.row.name ?? params.row.id}
              editActionLabel={getEntityActionLabel('edit', 'Suite Run')}
              deleteActionLabel={getEntityActionLabel('delete', 'Suite Run')}
              onEdit={() => {
                setEditTarget(params.row);
              }}
              onDelete={() => {
                onDeleteRow(params.row);
              }}
              disableEdit={
                editEditor.isPending ||
                deleteRowPendingId === params.row.id ||
                updateRowTagsColorPendingId === params.row.id
              }
              disableDelete={deleteRowPendingId === params.row.id}
            />
          );
        },
      },
    ],
    [
      deleteRowPendingId,
      disableLinks,
      disableSummaryLinks,
      disableActionsPopover,
      disableOutputTooltip,
      editEditor.isPending,
      mergedTagColorByName,
      onDeleteRow,
      setSelectedProjectId,
      updateRowTagsColorPendingId,
    ],
  );
  const resolvedColumns = useMemo(() => {
    const visibleColumns = columns.filter((column) => {
      if (hideNameColumn && column.field === 'name') return false;
      if (hideDetailsColumn && column.field === 'runId') return false;
      if (hideActionsColumn && column.field === 'actions') return false;
      return true;
    });
    if (!runColumnFirst) {
      return visibleColumns;
    }
    const runColumn = visibleColumns.find((column) => column.field === 'runNumber');
    const remainingColumns = visibleColumns.filter((column) => column.field !== 'runNumber');
    return runColumn ? [runColumn, ...remainingColumns] : visibleColumns;
  }, [columns, hideActionsColumn, hideDetailsColumn, hideNameColumn, runColumnFirst]);
  const togglableColumnFields = useMemo(
    () => resolvedColumns.map((column) => column.field).filter((field) => field !== 'name'),
    [resolvedColumns],
  );
  const gridConfig = suiteRunsGridConfig({
    organisationId: selectedOrganisationId,
    projectId: selectedProjectId,
    includeSummary,
    syncStateToUrl,
    persistKey: persistKey === undefined ? 'suite-runs-table' : persistKey,
    fixedFilters,
    initialSortModel,
    initialFilterModel,
  });
  const gridProps = buildTableGridProps<SuiteRunDto>({
    gridConfig,
    queryFn,
    queryKey,
    pageSizeOptions,
    initialPageSize,
    disablePageSizeSelect,
    hideFooter,
    noRowsText,
    onRowsChange,
    onQueryInput,
    onDataUpdatedAt,
    showLastUpdated,
    showToolbar,
    chrome: 'embedded',
  });
  return (
    <>
      <ServerDataTable<SuiteRunDto>
        queryPath="suite-runs/query"
        {...gridConfig}
        {...gridProps}
        initialData={initialData}
        columns={resolvedColumns}
        getRowId={(row) => row.id}
        isAuthenticated={isAuthenticated}
        organisationId={selectedOrganisationId}
        projectId={selectedProjectId}
        onQueryMeta={onQueryMeta}
        search={search}
        columnVisibilityModel={columnVisibilityModel}
        disableColumnMenu={disableColumnMenu}
        disableColumnSorting={disableColumnSorting}
        interactionMode="static"
        getRowHeight={dynamicRowHeight ? () => 'auto' : undefined}
        skeletonRowHeight={resolvedRowHeight}
        showSkeletonOnFetch={showPaginationSkeletonWhenRowsVisible}
        showPaginationSkeletonWhenRowsVisible={showPaginationSkeletonWhenRowsVisible}
        preservePreviousData={preservePreviousData}
        active={active}
        autoHeight
        gridProps={{
          getEstimatedRowHeight: dynamicRowHeight ? () => resolvedEstimatedRowHeight : undefined,
          rowHeight: dynamicRowHeight ? undefined : resolvedRowHeight,
          onColumnVisibilityModelChange,
          slotProps: {
            toolbar: {
              showQuickFilter: true,
              csvOptions: { disableToolbarButton: true },
              printOptions: { disableToolbarButton: true },
            },
            columnsManagement: {
              disableShowHideToggle: true,
              disableResetButton: true,
              getTogglableColumns: () => togglableColumnFields,
            },
          },
        }}
        sx={[
          ...(Array.isArray(tableSx) ? tableSx : [tableSx]),
          {
            '& .MuiDataGrid-row': {
              minHeight: `${resolvedRowHeight}px !important`,
            },
            '& .MuiDataGrid-cell': {
              minHeight: `${resolvedRowHeight}px !important`,
            },
            '& .MuiDataGrid-cell[data-field="status"], & .MuiDataGrid-cell[data-field="tests"], & .MuiDataGrid-cell[data-field="runNumber"], & .MuiDataGrid-cell[data-field="runId"], & .MuiDataGrid-cell[data-field="actions"]':
              {
                alignItems: 'flex-start',
              },
          },
        ]}
      />
      <SuiteRunEditDialog
        open={editTarget !== null}
        execution={editTarget}
        loading={editEditor.isPending}
        onClose={() => {
          if (editEditor.isPending) return;
          setEditTarget(null);
        }}
        onSave={async ({ tags, colors, status, timestamp, durationMs }) => {
          await editEditor.save({ tags, colors, status, timestamp, durationMs });
          setEditTarget(null);
        }}
      />
      <ConfirmDialog
        open={deleteTarget !== null}
        title={getDialogMessage('destructive.delete.title', { entity: 'suiteRun' })}
        description="This suite run and its associated test runs will be deleted."
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', { entity: 'suiteRun' })}
        confirmColor="error"
        loading={deleteLoading}
        onClose={onDeleteClose}
        onConfirm={onDeleteConfirm}
      />
    </>
  );
};

export const SuiteRunsTable = memo(SuiteRunsTableComponent);
